import { type MetaFunction } from '@remix-run/node'

export const meta: MetaFunction = () => [
	{ title: 'Kogia | Terms of Service (Users)' },
]

export default function MembersTermsOfServiceRoute() {
	return (
		<div className="mx-auto flex max-w-screen-lg flex-col gap-12 p-4 pb-20 md:pt-10">
			<div className="flex flex-col gap-2">
				<h2 className="mb-4 font-serif text-h2">
					<strong className="font-bold">Terms of Service (Users)</strong>
				</h2>
				<p>
					<em>Last updated: Feb 1, 2026</em>
				</p>
				<p>
					<strong className="text-h5 font-bold">
						In brief: What you can and can&rsquo;t use Kogia&rsquo;s media for
					</strong>
				</p>
				<p>
					<strong>
						You <em>can</em> use the media for:
					</strong>
				</p>
				<ul className="list-inside list-[auto]">
					<li>
						Non-commercial education, conservation, and awareness projects
					</li>
					<li>
						Nonprofit fundraising materials (reports, proposals, pitches, videos)
					</li>
					<li>
						Social media for advocacy, education, or conservation
					</li>
					<li>Nonprofit websites and online publications</li>
					<li>Educational, non-profit exhibitions and printed materials</li>
					<li>Conservation, awareness, or activist campaigns</li>
					<li>Small-scale, non-commercial art projects</li>
				</ul>
				<p>
					<strong>
						You <em>cannot</em> use the media for:
					</strong>
				</p>
				<ul className="list-inside list-[auto]">
					<li>Any commercial or for-profit purpose</li>
					<li>Selling, reselling, sharing, or redistributing the files</li>
					<li>For-profit schools, tours, training programs, or services</li>
					<li>For-profit art or museum exhibitions</li>
					<li>Prints, merchandise, or products for sale</li>
					<li>Removing or altering credits or watermarks</li>
					<li>Implying endorsement by Kogia or the creators</li>
				</ul>
				<p>
					<strong>Important:</strong>
				</p>
				<ul className="list-inside list-[auto]">
					<li>
						All use requires <strong>clear credit to both</strong>{' '}
						<strong>Kogia</strong> and the <strong>original contributor</strong>
					</li>
					<li>
						This is a <strong>non-commercial, revocable license</strong> for{' '}
						<strong>approved members</strong> <strong>only</strong>
					</li>
				</ul>
				<p>
					<strong className="text-h5 font-bold">Image Licensing Agreement</strong>
				</p>
				<p>
					Please read the following terms of service carefully. By ticking the
					box for &ldquo;I agree to the terms and conditions herein,&rdquo; you
					acknowledge that you have read, understood, and agree to be legally
					bound by this Agreement.
				</p>
				<p>
					<strong className="text-h5 font-bold">1. Service Description</strong>
				</p>
				<p>
					Kogia&rsquo;s Oceanic Library, operated by Kogia, is a nonprofit
					media library providing access to marine photography, video, and audio
					content (&ldquo;Library Content&rdquo;). The Library exists to support
					conservation organizations, nonprofits, scientists, researchers,
					activists, educators, and artists working to protect our oceans and
					marine life.
				</p>
				<p>
					The Library Content is donated largely by our community of underwater
					photographers and filmmakers (&ldquo;Contributors&rdquo;), who retain
					full copyright ownership of their work.
				</p>
				<p>
					<strong className="text-h5 font-bold">2. Definitions</strong>
				</p>
				<p>For the purposes of this Agreement:</p>
				<p>
					<strong>&ldquo;Library Content&rdquo;</strong> means all photographs,
					videos, audio recordings, and related media made available through
					Kogia&rsquo;s Oceanic Library.
				</p>
				<p>
					<strong>&ldquo;Contributor&rdquo;</strong> means the individual
					creator who retains copyright ownership of the Library Content.
				</p>
				<p>
					<strong>&ldquo;User&rdquo;</strong> means any individual or
					organization granted membership to Kogia&rsquo;s Oceanic Library.
				</p>
				<p>
					<strong>&ldquo;Non-Commercial Use&rdquo;</strong> means use that is not
					primarily intended for or directed toward commercial advantage, monetary
					compensation, paid promotion, or the sale of goods or services.
				</p>
				<p>
					<strong className="text-h5 font-bold">3. Usage Terms</strong>
				</p>
				<p>
					<strong>3.1 Permitted Uses</strong>
				</p>
				<p>
					User may use the Library Content only for{' '}
					<strong>Non-Commercial Uses</strong>, including:
				</p>
				<ul className="list-inside list-[auto]">
					<li>Non-commercial educational, conservation, or awareness videos</li>
					<li>
						Nonprofit fundraising materials (videos, pitches, proposals, and
						reports)
					</li>
					<li>Social Media for conservation, education, or advocacy</li>
					<li>Websites and online publications for nonprofit initiatives</li>
					<li>Educational or nonprofit exhibitions</li>
					<li>Educational printed materials</li>
					<li>Awareness, conservation, or activist campaigns</li>
					<li>Small-scale, non-commercial art projects</li>
				</ul>
				<p>
					<strong>3.2 Prohibited Uses</strong>
				</p>
				<p>User shall not, under any circumstances:</p>
				<ul className="list-inside list-[auto]">
					<li>
						Sell, license, sublicense, distribute, or give away Library Content
						to any third party
					</li>
					<li>
						Use Library Content for commercial purposes or within commercial
						ventures
					</li>
					<li>
						Use Library Content for for-profit schools, tour operators, training
						programs, or services
					</li>
					<li>
						Use Library Content in for-profit art or museum exhibitions
					</li>
					<li>Sell or distribute Library Content as prints or merchandise</li>
					<li>Remove or alter copyright notices or watermarks</li>
					<li>
						Use media sourced from outside the Oceanic Library section of
						Kogia&rsquo;s website
					</li>
					<li>
						Imply endorsement, partnership, or sponsorship by Kogia or any
						Contributor without written permission
					</li>
				</ul>
				<p>
					<strong className="text-h5 font-bold">4. Licensing Framework</strong>
				</p>
				<p>
					Library Content is made available under a limited, revocable,
					non-exclusive, non-transferable, non-sublicensable, nonprofit-only
					content license, granted by Kogia to vetted members, with mandatory
					attribution and no commercial rights.
				</p>
				<p>
					<strong className="text-h5 font-bold">
						5. Credit &amp; Attribution (Mandatory)
					</strong>
				</p>
				<p>
					<strong>5.1 Attribution Requirement</strong>
				</p>
				<p>
					Proper credit is a <strong>mandatory condition</strong> of this
					license. Failure to comply with the attribution requirements below
					constitutes a <strong>material breach</strong> of this Agreement.
				</p>
				<p>
					<strong>5.2 Copyright Ownership &amp; Moral Rights</strong>
				</p>
				<p>
					All Library Content remains the exclusive intellectual property of the
					respective Contributor.
				</p>
				<p>
					User agrees to respect the moral rights of Contributors, including the
					right of attribution and the right to object to misleading, derogatory,
					or inappropriate use of their work.
				</p>
				<p>
					<strong>5.3 Standard Credit Format</strong>
				</p>
				<p>
					Unless otherwise specified by Kogia in writing, all use of Library
					Content must include <strong>both</strong> the Contributor and Kogia,
					using the following format or a clear equivalent:
				</p>
				<p>
					<strong>[Contributor Name] / Kogia</strong>
				</p>
				<p>or</p>
				<p>
					<strong>Kogia / [Contributor Name]</strong>
				</p>
				<p>
					<strong>5.4 Photos (Still Images)</strong>
				</p>
				<p>
					<strong>a) Watermarked Images</strong> Kogia&rsquo;s official
					watermarked versions of Library Content constitute proper and
					sufficient attribution for both the Contributor and Kogia.
				</p>
				<ul className="list-inside list-[auto]">
					<li>
						Custom watermarks also constitute proper and sufficient attribution
						provided they clearly follow one of the following formats:
						<ul className="ml-4 list-inside list-[auto]">
							<li>
								<strong>[Contributor Name] / Kogia</strong>
							</li>
							<li>
								<strong>Kogia / [Contributor Name]</strong>
							</li>
						</ul>
					</li>
					<li>
						For social media use of watermarked images, the User must
						additionally:
						<ul className="ml-4 list-inside list-[auto]">
							<li>
								Tag Kogia&rsquo;s official account where technically possible,
								and
							</li>
							<li>Tag or clearly name the Contributor.</li>
						</ul>
					</li>
				</ul>
				<p>
					<strong>b) Non-Watermarked Images</strong>
				</p>
				<p>
					Use of non-watermarked files does not waive attribution requirements.
					In such cases, attribution is mandatory as follows:
				</p>
				<ul className="list-inside list-[auto]">
					<li>
						<strong>Social Media:</strong>
						<ul className="ml-4 list-inside list-[auto]">
							<li>
								Credit must appear in the caption or first comment, and
							</li>
							<li>
								User must tag both Kogia and the Contributor where technically
								possible.
							</li>
						</ul>
					</li>
					<li>
						<strong>Websites &amp; Online Publications:</strong>
						<ul className="ml-4 list-inside list-[auto]">
							<li>
								Credit must appear adjacent to the image or in a clearly
								visible credits section.
							</li>
						</ul>
					</li>
					<li>
						<strong>Educational &amp; Printed Materials:</strong>
						<ul className="ml-4 list-inside list-[auto]">
							<li>
								Credit must appear near the image or in a dedicated credits
								section and must be legible.
							</li>
						</ul>
					</li>
					<li>
						<strong>Exhibitions &amp; Installations:</strong>
						<ul className="ml-4 list-inside list-[auto]">
							<li>
								Credits must appear on wall labels, captions, or accompanying
								materials where credits are customarily provided.
							</li>
						</ul>
					</li>
				</ul>
				<p>
					<strong>5.5 Video &amp; Film</strong>
				</p>
				<ul className="list-inside list-[auto]">
					<li>
						Where video credits are included, User must credit both Kogia and
						the Contributor in the end credits.
					</li>
					<li>
						Where applicable (including online platforms), the video
						description must include:
						<br />
						<br />
						<em>
							Footage courtesy of Kogia&rsquo;s Oceanic Library &mdash; [Contributor
							Name]
						</em>
					</li>
					<li>
						For social media distribution of video content, User must tag both
						Kogia and the Contributor.
					</li>
				</ul>
				<p>
					<strong>5.6 Attribution Persistence</strong>
				</p>
				<p>
					Attribution must remain intact for the entire duration of the
					media&rsquo;s use. Removal or alteration of credits after publication
					constitutes misuse.
				</p>
				<p>
					<strong className="text-h5 font-bold">
						6. Membership Duration &amp; Media Usage
					</strong>
				</p>
				<p>
					<strong>6.1 Membership Duration</strong>
				</p>
				<p>
					Users may access and use Library Content only for the duration of an
					active and valid membership.
				</p>
				<p>
					<strong>6.2 Termination of Use</strong>
				</p>
				<p>
					Upon termination of membership, whether initiated by the User or
					by Kogia, the User immediately forfeits all rights to use any Library
					Content.
				</p>
				<p>
					<strong>6.3 Deletion Requirement</strong>
				</p>
				<p>
					User must promptly delete or cease using all Library Content obtained
					from Kogia&rsquo;s Oceanic Library upon termination.
				</p>
				<p>
					<strong className="text-h5 font-bold">
						7. Misuse, Remedies &amp; Enforcement
					</strong>
				</p>
				<p>Misuse of Library Content or violation of these Terms may result in:</p>
				<ul className="list-inside list-[auto]">
					<li>Immediate revocation of Library access</li>
					<li>Requirement to remove media from all platforms</li>
					<li>Termination of membership</li>
					<li>Injunctive relief to prevent further misuse</li>
					<li>Potential legal action for unauthorized or commercial use</li>
				</ul>
				<p>
					<strong className="text-h5 font-bold">8. Termination</strong>
				</p>
				<p>
					Either party has the right to terminate the membership at any point,
					with or without cause.
				</p>
				<p>
					<strong className="text-h5 font-bold">
						9. Governing Law &amp; Jurisdiction
					</strong>
				</p>
				<p>
					This Agreement shall be governed by and construed in accordance with
					the laws of the United States of America. Any disputes arising under
					this Agreement shall be subject to the exclusive jurisdiction of the
					state or federal courts located in the United States, at Kogia&rsquo;s
					election.
				</p>
			</div>
		</div>
	)
}
